import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Plus, Pencil, Trash2, Image as ImageIcon, Sparkles, GripVertical,
  Quote, Eye, EyeOff, Upload, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImage = (img) => (img && img.startsWith('/api/') ? `${API_URL}${img}` : img);

const ACCENT_SWATCHES = [
  { name: 'Pink', value: '#FF3B6B' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Yellow', value: '#FFD166' },
  { name: 'Teal', value: '#06D6A0' },
  { name: 'Orange', value: '#FB923C' },
];

const emptyForm = { image: '', child_name: '', age: 7, message: '', accent: '#FF3B6B', order: 0, active: true };

export const CrmHeroTestimonials = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // testimonial being edited (null = create)
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const fileInputRef = React.useRef(null);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/hero-testimonials`, { withCredentials: true });
      setItems(res.data || []);
    } catch { toast.error('Failed to load testimonials'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      image: t.image || '', child_name: t.child_name || '', age: t.age || 7,
      message: t.message || '', accent: t.accent || '#FF3B6B',
      order: t.order || 0, active: !!t.active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.image || !form.child_name || !form.message) {
      toast.error('Image URL, name, and message are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API_URL}/api/admin/hero-testimonials/${editing.testimonial_id}`, form, { withCredentials: true });
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API_URL}/api/admin/hero-testimonials`, form, { withCredentials: true });
        toast.success('Testimonial added');
      }
      setOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try {
      await axios.patch(`${API_URL}/api/admin/hero-testimonials/${t.testimonial_id}`,
        { active: !t.active }, { withCredentials: true });
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.child_name}'s testimonial?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/hero-testimonials/${t.testimonial_id}`, { withCredentials: true });
      toast.success('Testimonial deleted');
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  // ─── Image upload ────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error('Only JPG / PNG / WEBP images are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API_URL}/api/admin/hero-testimonials/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((p) => ({ ...p, image: res.data?.url || '' }));
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  // ─── Drag-to-reorder cards ───────────────────────────────────
  const persistOrder = async (next) => {
    const payload = next.map((t, i) => ({ testimonial_id: t.testimonial_id, order: i }));
    try {
      await axios.patch(`${API_URL}/api/admin/hero-testimonials/reorder`,
        { order: payload }, { withCredentials: true });
      toast.success('Order saved');
    } catch { toast.error('Failed to save order'); }
  };
  const onCardDragStart = (idx) => (e) => { setDragIndex(idx); e.dataTransfer.effectAllowed = 'move'; };
  const onCardDragOver = (idx) => (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onCardDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) return;
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    setItems(next);
    setDragIndex(null);
    persistOrder(next);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Hero Testimonials</h1>
          <p className="text-[#6B6B7A]">Manage the landing-page carousel. {items.length === 0 ? 'You have no custom slides — the hero shows built-in defaults.' : `${items.filter((i) => i.active).length} of ${items.length} slides are live.`}</p>
        </div>
        <Button onClick={openCreate} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="create-testimonial-btn">
          <Plus className="w-4 h-4 mr-2" /> New Testimonial
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-12 text-center">
          <Sparkles className="w-12 h-12 text-[#6B6B7A] mx-auto mb-4" />
          <p className="text-[#6B6B7A] mb-4">No testimonials yet. The landing page is showing built-in defaults.</p>
          <p className="text-xs text-[#9a9aa6]">Once you add one, <strong>only your custom slides</strong> will appear on the hero carousel — the defaults are used only as a fallback.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t, i) => (
            <motion.div key={t.testimonial_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              draggable
              onDragStart={onCardDragStart(i)}
              onDragOver={onCardDragOver(i)}
              onDrop={onCardDrop(i)}
              className={`bg-white rounded-2xl border overflow-hidden card-hover cursor-move ${t.active ? 'border-[#E8E5DF]' : 'border-gray-200 opacity-70'} ${dragIndex === i ? 'ring-2 ring-[#FF3B6B]' : ''}`}
              data-testid={`testimonial-card-${t.testimonial_id}`}
            >
              <div className="relative w-full aspect-[4/3] overflow-hidden">
                <img src={absoluteImage(t.image)} alt="" className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Image+broken'; }} />
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: t.accent + '22', color: t.accent }}>
                  {t.accent}
                </span>
                <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-white/80 text-[#1E1E24] backdrop-blur flex items-center gap-1" title="Drag card to reorder">
                  <GripVertical className="w-3 h-3" /> drag
                </span>
              </div>
              <div className="p-4">
                <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{t.child_name}'s {t.age}th Birthday</p>
                <p className="text-xs text-[#6B6B7A] line-clamp-3 mt-1 italic">"{t.message}"</p>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="text-xs text-[#9a9aa6] flex items-center gap-1">Order {t.order}</div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); toggleActive(t); }} className="h-8 w-8 p-0"
                      title={t.active ? 'Deactivate' : 'Activate'} data-testid={`toggle-active-${t.testimonial_id}`}>
                      {t.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-[#9a9aa6]" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(t); }} className="h-8 w-8 p-0" data-testid={`edit-${t.testimonial_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(t); }} className="h-8 w-8 p-0 text-red-500" data-testid={`delete-${t.testimonial_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="testimonial-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }} className="flex items-center gap-2 text-xl">
              <Quote className="w-5 h-5 text-[#FF3B6B]" /> {editing ? 'Edit testimonial' : 'New testimonial'}
            </DialogTitle>
            <DialogDescription>These slides appear on the landing-page hero carousel. Auto-rotate every 4 seconds.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Slide image</Label>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                className="mt-1 rounded-xl border-2 border-dashed border-[#E8E5DF] hover:border-[#FF3B6B] bg-[#FFFDF8] p-4 flex flex-col items-center justify-center text-center"
                data-testid="form-drop-zone"
              >
                {form.image ? (
                  <div className="w-full">
                    <img src={absoluteImage(form.image)} alt="preview" className="w-full h-40 object-cover rounded-lg"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <p className="text-[11px] text-[#9a9aa6] font-mono mt-2 truncate">{form.image}</p>
                  </div>
                ) : (
                  <div className="py-6 text-[#6B6B7A]">
                    <Upload className="w-7 h-7 mx-auto mb-1 text-[#FF3B6B]" />
                    <p className="text-sm">Drop an image here</p>
                    <p className="text-xs text-[#9a9aa6]">JPG · PNG · WEBP · max 2MB</p>
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    ref={fileInputRef}
                    className="hidden"
                    data-testid="form-file-input"
                  />
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="rounded-full h-8 text-xs" data-testid="form-pick-file">
                    {uploading ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" />Uploading…</>) : (<><Upload className="w-3 h-3 mr-1" />Choose file</>)}
                  </Button>
                  {form.image && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setForm((p) => ({ ...p, image: '' }))} className="text-red-500 h-8 text-xs">
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-[#9a9aa6] mt-1">Or paste a URL:</p>
              <Input value={form.image} onChange={(e) => setForm((p) => ({ ...p, image: e.target.value }))}
                placeholder="https://…" className="mt-1 rounded-xl border-[#E8E5DF] text-xs" data-testid="form-image" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-semibold">Child's name</Label>
                <Input value={form.child_name} onChange={(e) => setForm((p) => ({ ...p, child_name: e.target.value }))}
                  placeholder="Emma" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="form-child-name" />
              </div>
              <div>
                <Label className="font-semibold">Age</Label>
                <Input type="number" min="1" max="25" value={form.age}
                  onChange={(e) => setForm((p) => ({ ...p, age: parseInt(e.target.value) || 1 }))}
                  className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="form-age" />
              </div>
            </div>
            <div>
              <Label className="font-semibold">Quote / message</Label>
              <Textarea value={form.message} onChange={(e) => setForm((p) => ({ ...p, message: e.target.value.slice(0, 400) }))}
                rows={3} placeholder="A short testimonial from the parent…" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="form-message" />
              <p className="text-xs text-[#9a9aa6] mt-1">{(form.message || '').length}/400</p>
            </div>
            <div>
              <Label className="font-semibold">Accent color</Label>
              <div className="mt-2 flex gap-2 flex-wrap" data-testid="form-accent-swatches">
                {ACCENT_SWATCHES.map((s) => (
                  <button key={s.value} type="button" title={s.name}
                    onClick={() => setForm((p) => ({ ...p, accent: s.value }))}
                    className={`w-8 h-8 rounded-full border-2 ${form.accent === s.value ? 'border-[#1E1E24]' : 'border-white shadow-sm'}`}
                    style={{ backgroundColor: s.value }} data-testid={`accent-${s.value.replace('#','')}`} />
                ))}
                <Input value={form.accent} onChange={(e) => setForm((p) => ({ ...p, accent: e.target.value }))}
                  className="w-28 h-8 rounded-full text-xs font-mono" data-testid="form-accent" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="font-semibold">Display order</Label>
                <Input type="number" value={form.order}
                  onChange={(e) => setForm((p) => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                  className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="form-order" />
              </div>
              <label className="flex items-center gap-2 text-sm bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3 cursor-pointer">
                <Switch checked={form.active} onCheckedChange={(v) => setForm((p) => ({ ...p, active: v }))} data-testid="form-active" />
                Active (show on landing)
              </label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-full" disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving}
                className="flex-1 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="form-save">
                {saving ? 'Saving…' : (editing ? 'Save changes' : 'Add testimonial')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmHeroTestimonials;
